import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

let scriptPromise = null;
function loadScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * <TurnstileField ref={ref} onVerify={(token) => ...} />
 *
 * Usage:
 *  - wraps Cloudflare Turnstile widget with explicit render
 *  - exposes imperative `reset()` via ref
 *  - calls onVerify(token) on success, onExpire/onError to clear
 *  - if REACT_APP_TURNSTILE_SITE_KEY is missing (local dev), renders a noop placeholder
 *    that auto-resolves with a debug token so the form still submits.
 */
const TurnstileField = forwardRef(function TurnstileField(
  { onVerify, onExpire, onError, theme = "light", size = "normal" },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    // Dev fallback: no site key configured → bypass widget but emit a token.
    if (!siteKey) {
      onVerify && onVerify("dev-skip");
      setStatus("ready");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    loadScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || !turnstile) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => onVerify && onVerify(token),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": () => onError && onError(),
        });
        setStatus("ready");
      })
      .catch(() => {
        setStatus("error");
        onError && onError();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  // Hide entirely in dev mode (no site key) to avoid empty space in UI.
  if (!siteKey) return null;

  return (
    <div
      className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-4"
      data-testid="turnstile-field"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-[#6B7280] mb-4">
        <ShieldCheck size={14} className="text-[#519872]" />
        Verificare anti-spam (Cloudflare)
      </div>
      {status === "loading" && (
        <div
          className="flex items-center justify-center gap-2 text-xs text-[#6B7280] py-2"
          data-testid="turnstile-loading"
        >
          <Loader2 size={14} className="animate-spin" /> Se încarcă verificarea…
        </div>
      )}
      {status === "error" && (
        <div
          className="text-xs text-[#9b1f2c] text-center py-2"
          data-testid="turnstile-error"
        >
          Verificarea anti-spam nu a putut fi încărcată. Reîncarcă pagina.
        </div>
      )}
      {/* Widget area — centered, fit-content so the Cloudflare iframe never stretches */}
      <div className="flex justify-center w-full overflow-hidden">
        <div
          ref={containerRef}
          className="inline-block max-w-full"
          aria-hidden={status !== "ready"}
        />
      </div>
    </div>
  );
});

export default TurnstileField;
